import { capitalize } from "lodash";

export const formatByteSize = (numBytes: number) => {
  if (numBytes < 1024) return `${numBytes} B`;
  if (numBytes < 1024 * 1024) return `${(numBytes / 1024).toFixed(1)} KB`;
  return `${(numBytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatEnum = (value?: string | null) => {
  if (!value) return "";
  return value.split("_").map(capitalize).join(" ");
};

const FILING_STATUS_ABBREV: Record<string, string> = {
  single: "Single",
  married_filing_jointly: "MFJ",
  married_filing_separately: "MFS",
  head_of_household: "HoH",
  qualifying_surviving_spouse: "QSS",
};

export const formatFilingStatusAbbrev = (value?: string | null) => {
  if (!value) return "";
  return FILING_STATUS_ABBREV[value] ?? formatEnum(value);
};

export const formatDollarShorthand = (
  value: number,
  {
    minimumFractionDigitsForThousands = 0,
    maximumFractionDigitsForThousands = 0,
    fractionDigitsForMillions = 0,
    fractionDigitsForBillions = 0,
  }: {
    minimumFractionDigitsForThousands?: number;
    maximumFractionDigitsForThousands?: 0 | 1 | 2;
    fractionDigitsForMillions?: number;
    fractionDigitsForBillions?: number;
  } = {},
): string => {
  if (Math.abs(value) >= 1e9) {
    return `$${(value / 1e9).toFixed(fractionDigitsForBillions)}B`;
  } else if (Math.abs(value) >= 1e6) {
    return `$${(value / 1e6).toFixed(fractionDigitsForMillions)}M`;
  } else if (Math.abs(value) >= 1e3) {
    const divided = value / 1e3;
    return `$${divided.toLocaleString("en-US", {
      minimumFractionDigits: minimumFractionDigitsForThousands,
      maximumFractionDigits: maximumFractionDigitsForThousands,
      useGrouping: false,
    })}k`;
  }

  return `$${value.toFixed(0)}`;
};

export const formatDollarLongHand = (
  value?: number,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  }: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {},
): string => {
  // Zero is falsy in JS, so we need to check for it explicitly.
  if (value !== 0 && !value) return "";

  const format = (num: number, minFractionDigits: number) =>
    num.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits,
    }) || "";

  const formatted = format(value, minimumFractionDigits);

  // If the value has a decimal part that would display as a single digit,
  // either round to whole number or show with 2 decimal places
  // Never show $100.2. Only ever show $100 or $100.20
  const parsed = parseNumberFromCurrencyStr(formatted);
  const endsWithTenthsPlace = parsed % 1 !== 0 && (parsed * 10) % 1 === 0;
  if (endsWithTenthsPlace && maximumFractionDigits === 2) {
    return format(value, 2);
  }

  return formatted;
};

export const formatPercent = (
  value: number,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  }: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string => {
  return value.toLocaleString("en-US", {
    style: "percent",
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

export const formatNumberWithCommas = (value: number): string => {
  return value.toLocaleString("en-US");
};

export function parseNumberFromCurrencyStr(value: string): number {
  // Remove currency symbols, commas, and other non-numeric characters except decimal point
  const cleanedValue = value.replace(/[^0-9.-]/g, "");
  return Number(cleanedValue);
}

export function formatArrayAsSentence(array: string[]): string {
  if (array.length === 0) {
    return "";
  }

  if (array.length === 1) {
    return array[0];
  }

  return `${array.slice(0, -1).join(", ")} and ${array[array.length - 1]}`;
}

export const formatPhoneNumber = (phoneNumber: string): string => {
  if (phoneNumber.length === 10) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
  }
  if (phoneNumber.length > 10) {
    const countryCodeLength = phoneNumber.length - 10;
    return `+${phoneNumber.slice(0, countryCodeLength)} (${phoneNumber.slice(countryCodeLength, countryCodeLength + 3)}) ${phoneNumber.slice(countryCodeLength + 3, countryCodeLength + 6)}-${phoneNumber.slice(countryCodeLength + 6)}`;
  }
  return phoneNumber;
};

export const formatFullName = ({
  firstName,
  middleInitial,
  lastName,
}: {
  firstName: string;
  middleInitial?: string | null;
  lastName: string;
}): string => {
  return `${firstName} ${middleInitial ? `${middleInitial}. ` : ""}${lastName}`;
};

type Address = {
  street: string;
  aptNo?: string | null;
  city: string;
  state: string;
  zip: string;
};

export const formatAddress = (address: Address): string => {
  return `${address.street}${address.aptNo ? ` Apt. ${address.aptNo}` : ""}, ${address.city}, ${address.state} ${address.zip}`;
};

export function formatRealPropertyUseType(
  useType: string | null | undefined,
): string {
  switch (useType) {
    case "primary_residence":
      return "Primary Residence";
    case "secondary_residence":
      return "Secondary Residence";
    case "rental":
      return "Rental Property";
    case "short_term_rental":
      return "Short-Term Rental";
    case "mixed_use":
      return "Mixed Use";
    case "investment":
      return "Investment";
    case "home_office":
      return "Home Office";
    default:
      return useType?.replace(/_/g, " ") ?? "";
  }
}

const TAX_ADVANTAGED_ACCOUNT_PLAN_TYPE_LABELS: Record<string, string> = {
  traditional_ira: "Traditional IRA",
  roth_ira: "Roth IRA",
  sep_ira: "SEP IRA",
  traditional_simple_ira: "Traditional SIMPLE IRA",
  roth_simple_ira: "Roth SIMPLE IRA",
  traditional_401k: "Traditional 401(k)",
  roth_401k: "Roth 401(k)",
  traditional_403b: "Traditional 403(b)",
  roth_403b: "Roth 403(b)",
  traditional_457b: "Traditional 457(b)",
  roth_457b: "Roth 457(b)",
  pension: "Pension",
  hsa: "HSA",
  other: "Other",
};

export function formatTaxAdvantagedAccountPlanType(
  planType: string | null | undefined,
): string {
  if (!planType) return "—";
  return TAX_ADVANTAGED_ACCOUNT_PLAN_TYPE_LABELS[planType] ?? "—";
}
